import {
    LoginByCodeErrorKey,
    LoginByPasswordErrorKey,
} from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useLogin/types';
import { GenerateOtpResponseKey } from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/hooks/useOtp/types';
import type { OtpType } from 'src/models/otp';
import { TwoFactorType } from 'src/models/twoFactor';

import {
    AuthCredentialType,
    type ApplicantLoginFormValues,
    type ValidationResult,
    type Validator,
} from 'src/components/AccountLogin/CombinedLoginCards/ApplicantLoginCard/MultiStepForm/types';

export const OtpTypeMap: Record<AuthCredentialType, OtpType> = {
    [AuthCredentialType.Email]: 'email',
    [AuthCredentialType.Phone]: 'phone',
};

export const CREDENTIAL_TYPE_FIELD = 'credentialType';
export const USERNAME_FIELD = 'username';
export const PASSWORD_FIELD = 'password';
export const CODE_FIELD = 'code';
export const CODE_2FA_FIELD = 'code2fa';
export const PUSH_CODE_FIELD = 'pushCode';
export const FIRST_NAME_FIELD = 'firstName';
export const LAST_NAME_FIELD = 'lastName';
export const ADV_AGREEMENT_FIELD = 'advAgreement';
export const INACTIVE_ACCOUNT_DELETION_FIELD = 'deleteInactiveTime';
export const TRUST_THIS_SOURCE_FIELD = 'trust_this_source';

export const defaultValues: ApplicantLoginFormValues = {
    [CREDENTIAL_TYPE_FIELD]: AuthCredentialType.Phone,
    [USERNAME_FIELD]: '',
    [PASSWORD_FIELD]: '',
    [CODE_FIELD]: '',
    [CODE_2FA_FIELD]: '',
    [PUSH_CODE_FIELD]: '',
    [FIRST_NAME_FIELD]: '',
    [LAST_NAME_FIELD]: '',
    [ADV_AGREEMENT_FIELD]: false,
    [TRUST_THIS_SOURCE_FIELD]: false,
};

export const MISSING_VALUE_ERROR = 'MISSING_VALUE';
export const NOT_A_NUMBER_ERROR = 'NOT_A_NUMBER';
export const INVALID_EMAIL_ERROR = 'INVALID_EMAIL';
export const INVALID_PHONE_ERROR = 'INVALID_PHONE';

export const composeValidators =
    (...validators: Validator[]): Validator =>
    (value, allValues, meta) =>
        validators.reduce<ValidationResult | Promise<ValidationResult>>(
            (error, validator) => error || validator(value, allValues, meta),
            undefined
        );

export const required: Validator = (value) => (value?.trim() ? undefined : MISSING_VALUE_ERROR);

export const mustBeNumber: Validator = (value) => (isNaN(Number(value)) ? NOT_A_NUMBER_ERROR : undefined);

export const mustBeEmail: Validator = (value) => {
    const emailRegex = /^[a-zA-Z0-9а-яА-Я._%+-]+@[a-zA-Z0-9а-яА-Я.-]+\.[a-zA-Zа-яА-Я]{2,}$/;
    return value && emailRegex.test(value) ? undefined : INVALID_EMAIL_ERROR;
};

export const mustBePhone: Validator = (value) => {
    const phoneRegex = /(?:([+]\d{1,4})[-.\s]?)?(?:[(](\d{1,3})[)][-.\s]?)?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})/g;
    return value && phoneRegex.test(value) ? undefined : INVALID_PHONE_ERROR;
};

export const extractTwoFactorType = (value: LoginByPasswordErrorKey): TwoFactorType | null => {
    let twoFactorType = null;

    if (value === LoginByPasswordErrorKey.TwoFactorCheck) {
        twoFactorType = TwoFactorType.Code;
    }

    if (
        value === LoginByPasswordErrorKey.TwoFactorTotpCheck ||
        value === LoginByPasswordErrorKey.TwoFactorTotpCheckWrongCode
    ) {
        twoFactorType = TwoFactorType.Totp;
    }

    if (value === LoginByPasswordErrorKey.TwoFactorPushCheck) {
        twoFactorType = TwoFactorType.Push;
    }

    return twoFactorType;
};

export const isTwoFactorErrorKey = (errorKey?: string): boolean => {
    if (!errorKey) {
        return false;
    }

    const twoFactorErrorKeys = [
        LoginByPasswordErrorKey.TwoFactorCheck,
        LoginByPasswordErrorKey.TwoFactorTotpCheck,
        LoginByPasswordErrorKey.TwoFactorTotpCheckWrongCode,
        LoginByPasswordErrorKey.TwoFactorPushCheck,
    ];

    return twoFactorErrorKeys.includes(errorKey as LoginByPasswordErrorKey);
};

export const isPasswordRequiredErrorKey = (errorKey?: string): boolean => {
    if (!errorKey) {
        return false;
    }

    return errorKey === GenerateOtpResponseKey.PasswordRequired;
};

export const isAccountNotFoundErrorKey = (errorKey?: string): boolean => {
    if (!errorKey) {
        return false;
    }

    return errorKey === LoginByCodeErrorKey.AccountNotFound;
};

export const isCaptchaErrorKey = (errorKey?: string): boolean => {
    if (!errorKey) {
        return false;
    }

    return errorKey === GenerateOtpResponseKey.Captcha;
};

export const isTwoFactorAccountBlockedErrorKey = (errorKey?: string): boolean => {
    if (!errorKey) {
        return false;
    }

    return errorKey === LoginByPasswordErrorKey.TwoFactorAccountBlocked;
};
